import { Col, DatePicker, Form, Input, Row, Space } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { BaseSelect } from '@app/components/common/selects/BaseSelect/BaseSelect';
import { BaseInput } from '@app/components/common/inputs/BaseInput/BaseInput';
import { DropdownOption } from '@app/models/DropdownOption';
import React, { useEffect, useState } from 'react';
import ButtonGroup from './ButtonGroup';
import moment from 'moment';

type PreClearanceInfoProps = {
  editable: boolean;
  canEdit: boolean;
  status: string | null;
  type: 'edit' | 'create' | 'view';
  isStatusDoReview: boolean;
  acceptedFields: any;
  productCategories: DropdownOption[];
  countries: DropdownOption[];
  location_options: DropdownOption[];
  department_options: DropdownOption[];
  // eslint-disable-next-line @typescript-eslint/ban-types
  setAcceptedFields: Function;
  form: any;
  preClearanceData: any;
  preClearanceDetails: {
    job_id: string;
    job_type: any;
    status: string;
  };
};

const PreClearanceInfo: React.FC<PreClearanceInfoProps> = ({
  editable,
  canEdit,
  status,
  acceptedFields = {}, // Initialize acceptedFields as an empty object by default
  productCategories,
  countries,
  location_options,
  department_options,
  setAcceptedFields,
  form,
  preClearanceData,
}) => {
  const [disabledButtons, setDisabledButtons] = useState<Record<string, boolean>>({});
  const [visibleButtons, setButtonVisibility] = useState<Record<string, string>>({}); // New state for button visibility
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userType = user?.usertype;

  //const shouldShowButtonControls = type === 'edit' && userType === 'ADMIN';

  //const shouldShowStatusIcons = type === 'view' || type === 'edit';

  const canEditFields = editable && userType === 'CUSTOMER' && (status === 'DRAFT' || status === 'New');

  useEffect(() => {
    if (preClearanceData) {
      form.setFieldsValue({
        invoice_no: preClearanceData?.invoice_no || '',
        invoice_date: preClearanceData?.invoice_date || '',
        product_name: preClearanceData?.product_name || '',
        quantity: preClearanceData?.quantity || '',
        hs_code: preClearanceData?.hs_code || '',
        value_usd: preClearanceData?.value_usd || '',
        supplier_name: preClearanceData?.supplier_name || '',
        supplier_reg: preClearanceData?.supplier_reg || '',
        supplier_address: preClearanceData?.supplier_address || '',
        country_ids: preClearanceData?.country_ids || [],
        pre_clearance_category_id: preClearanceData?.pre_clearance_category_id || '',
        validity_period: preClearanceData?.validity_period || '',
        validity_exp: preClearanceData?.validity_exp || '',
        special_conditions: preClearanceData?.special_conditions || '',
        location_id: preClearanceData?.location_id || '',
        department_id: preClearanceData?.department_id || '',
        allowed_weightage: preClearanceData?.allowed_weightage || '',
        expires_at: preClearanceData?.expires_at || '',
      });

      setAcceptedFields(preClearanceData.accepted || {});
    }
  }, [form, preClearanceData, setAcceptedFields]);

  const handleButtonClick = (fieldName: string, action: string) => {
    if (acceptedFields[fieldName]) {
      return; // If already verified (disable button)
    }

    // Set the field to true or false depending on the action
    if (action === 'Check') {
      setAcceptedFields((prevState: any) => ({
        ...prevState,
        [fieldName]: true,
      }));
    } else if (action === 'Close') {
      setAcceptedFields((prevState: any) => ({
        ...prevState,
        [fieldName]: false,
      }));
    }

    setDisabledButtons((prevState) => ({
      ...prevState,
      [fieldName]: true,
    }));

    // Hide the clicked button and show the others
    setButtonVisibility((prevVisibility) => ({
      ...prevVisibility,
      [fieldName]: action.toLowerCase(),
    }));

    console.log(`${action} clicked for ${fieldName}`);
  };

  const fields: any = [
    {
      name: 'invoice_no',
      label: 'Proforma Invoice No',
      component: canEditFields ? (
        <Input.Group compact style={{ display: 'flex', width: '100%' }}>
          <Input
            style={{ width: '100%' }}
            value={form.getFieldValue('invoice_no')}
            onChange={(e) => form.setFieldsValue({ invoice_no: e.target.value })}
            placeholder="Enter Invoice No"
          />
          {/* {shouldShowButtonControls && (
            <ButtonGroup
              onCheck={() => handleButtonClick('invoice_no', 'Check')}
              onClose={() => handleButtonClick('invoice_no', 'Close')}
            />
          )} */}
        </Input.Group>
      ) : (
        <BaseInput readOnly value={form.getFieldValue('invoice_no')} />
      ),
    },
    {
      name: 'invoice_date',
      label: 'Proforma Invoice Date',
      component: canEditFields ? (
        <Input.Group compact style={{ display: 'flex', width: '100%' }}>
          <DatePicker
            // style={{ width: shouldShowButtonControls ? '100%' : '100%' }}
            value={form.getFieldValue('invoice_date') ? moment(form.getFieldValue('invoice_date')) : null}
            onChange={(date) => form.setFieldsValue({ invoice_date: date ? date.format('YYYY-MM-DD') : '' })}
            placeholder="Select Invoice Date"
            format="YYYY-MM-DD" // Customize date format as needed
          />
          {/* {shouldShowButtonControls && (
            <ButtonGroup
              onCheck={() => handleButtonClick('invoice_date', 'Check')}
              onClose={() => handleButtonClick('invoice_date', 'Close')}
            />
          )} */}
        </Input.Group>
      ) : (
        <BaseInput readOnly value={form.getFieldValue('invoice_date')} />
      ),
    },
    {
      name: 'product_name',
      label: 'Product Name',
      component: canEditFields ? (
        <Input.Group compact style={{ display: 'flex', width: '100%' }}>
          <Input
            // style={{ width: shouldShowButtonControls ? '100%' : '100%' }}
            value={form.getFieldValue('product_name')}
            onChange={(e) => form.setFieldsValue({ product_name: e.target.value })}
            placeholder="Enter Product Name"
          />
          {/* {shouldShowButtonControls && (
            <ButtonGroup
              onCheck={() => handleButtonClick('product_name', 'Check')}
              onClose={() => handleButtonClick('product_name', 'Close')}
            />
          )} */}
        </Input.Group>
      ) : (
        <BaseInput readOnly value={form.getFieldValue('product_name')} />
      ),
    },
    {
      name: 'quantity',
      label: 'Quantity',
      component: canEditFields ? (
        <Input.Group compact style={{ display: 'flex', width: '100%' }}>
          <Input
            style={{ width: '100%' }}
            value={form.getFieldValue('quantity')}
            onChange={(e) => form.setFieldsValue({ quantity: e.target.value })}
            placeholder="Enter Quantity"
          />
          {/* {shouldShowButtonControls && (
            <ButtonGroup
              onCheck={() => handleButtonClick('quantity', 'Check')}
              onClose={() => handleButtonClick('quantity', 'Close')}
            />
          )} */}
        </Input.Group>
      ) : (
        <BaseInput readOnly value={form.getFieldValue('quantity')} />
      ),
    },
    {
      name: 'hs_code',
      label: 'H. S. Code',
      component: canEditFields ? (
        <Input.Group compact style={{ display: 'flex', width: '100%' }}>
          <Input
            value={form.getFieldValue('hs_code')}
            onChange={(e) => form.setFieldsValue({ hs_code: e.target.value })}
            placeholder="Enter H.S. Code"
          />
          {/* {shouldShowButtonControls && (
            <ButtonGroup
              onCheck={() => handleButtonClick('hs_code', 'Check')}
              onClose={() => handleButtonClick('hs_code', 'Close')}
            />
          )} */}
        </Input.Group>
      ) : (
        <BaseInput readOnly value={form.getFieldValue('hs_code')} />
      ),
    },
    {
      name: 'value_usd',
      label: 'Value (US$)',
      component: canEditFields ? (
        <Input.Group compact style={{ display: 'flex', width: '100%' }}>
          <Input
            style={{ width: '100%' }}
            value={form.getFieldValue('value_usd')}
            onChange={(e) => form.setFieldsValue({ value_usd: e.target.value })}
            placeholder="Enter Value"
          />
          {/* {shouldShowButtonControls && (
            <ButtonGroup
              onCheck={() => handleButtonClick('value_usd', 'Check')}
              onClose={() => handleButtonClick('value_usd', 'Close')}
            />
          )} */}
        </Input.Group>
      ) : (
        <BaseInput readOnly value={form.getFieldValue('value_usd')} />
      ),
    },
    {
      name: 'supplier_name',
      label: 'Supplier Name',
      component: canEditFields ? (
        <Input.Group compact style={{ display: 'flex', width: '100%' }}>
          <Input
            style={{ width: '100%' }}
            value={form.getFieldValue('supplier_name')}
            onChange={(e) => form.setFieldsValue({ supplier_name: e.target.value })}
            placeholder="Enter Supplier Name"
          />
          {/* {shouldShowButtonControls && (
            <ButtonGroup
              onCheck={() => handleButtonClick('supplier_name', 'Check')}
              onClose={() => handleButtonClick('supplier_name', 'Close')}
            />
          )} */}
        </Input.Group>
      ) : (
        <BaseInput readOnly value={form.getFieldValue('supplier_name')} />
      ),
    },
    {
      name: 'supplier_reg',
      label: 'Supplier Registration No',
      component: canEditFields ? (
        <Input.Group compact style={{ display: 'flex', width: '100%' }}>
          <Input
            style={{ width: '100%' }}
            value={form.getFieldValue('supplier_reg')}
            onChange={(e) => form.setFieldsValue({ supplier_reg: e.target.value })}
            placeholder="Enter Supplier Registration No"
          />
          {/* {shouldShowButtonControls && (
            <ButtonGroup
              onCheck={() => handleButtonClick('supplier_reg', 'Check')}
              onClose={() => handleButtonClick('supplier_reg', 'Close')}
            />
          )} */}
        </Input.Group>
      ) : (
        <BaseInput readOnly value={form.getFieldValue('supplier_reg')} />
      ),
    },
    {
      name: 'supplier_address',
      label: 'Supplier Address',
      component: canEditFields ? (
        <Input.Group compact style={{ display: 'flex', width: '100%' }}>
          <Input
            style={{ width: '100%' }}
            value={form.getFieldValue('supplier_address')}
            onChange={(e) => form.setFieldsValue({ supplier_address: e.target.value })}
            placeholder="Enter Supplier Address"
          />
          {/* {shouldShowButtonControls && (
            <ButtonGroup
              onCheck={() => handleButtonClick('supplier_address', 'Check')}
              onClose={() => handleButtonClick('supplier_address', 'Close')}
            />
          )} */}
        </Input.Group>
      ) : (
        <BaseInput readOnly value={form.getFieldValue('supplier_address')} />
      ),
    },
    {
      name: 'allowed_weightage',
      label: 'Allowed Weightage',
      component: canEditFields ? (
        <Input.Group compact style={{ display: 'flex', width: '100%' }}>
          <Input
            style={{ width: '100%' }}
            value={form.getFieldValue('allowed_weightage')}
            onChange={(e) => form.setFieldsValue({ allowed_weightage: e.target.value })}
            placeholder="Enter Allowed Weightage"
          />
          {/* {shouldShowButtonControls && (
            <ButtonGroup
              onCheck={() => handleButtonClick('allowed_weightage', 'Check')}
              onClose={() => handleButtonClick('allowed_weightage', 'Close')}
            />
          )} */}
        </Input.Group>
      ) : (
        <BaseInput readOnly value={form.getFieldValue('allowed_weightage')} />
      ),
    },
  ];

  return (
    <>
      <Row>
        <BaseCol xs={24} md={24} lg={12}>
          <Form.Item
            label={
              <Space style={{ display: 'flex', alignItems: 'center' }}>
                {/* {shouldShowStatusIcons &&
                  (acceptedFields['pre_clearance_category_id'] === true ? (
                    <CheckCircleOutlined style={{ color: 'green', marginRight: '8px' }} />
                  ) : acceptedFields['pre_clearance_category_id'] === false ? (
                    <CloseCircleOutlined style={{ color: 'red', marginRight: '8px' }} />
                  ) : null)} */}
                Select Category
              </Space>
            }
            name="pre_clearance_category_id"
          >
            <Input.Group compact style={{ display: 'flex' }}>
              {canEditFields ? (
                <BaseSelect
                  placeholder="Select Category"
                  disabled={!canEditFields || !!acceptedFields['pre_clearance_category_id']}
                  options={productCategories}
                  value={form.getFieldValue('pre_clearance_category_id')}
                  style={{ width: '100%' }}
                  onChange={(value) => form.setFieldsValue({ pre_clearance_category_id: value })}
                />
              ) : (
                <BaseInput
                  readOnly
                  value={
                    productCategories.find(
                      (category) => category.value === form.getFieldValue('pre_clearance_category_id'),
                    )?.label || 'Not Selected'
                  }
                />
              )}
              {/* {shouldShowButtonControls && (
                <ButtonGroup
                  onCheck={() => handleButtonClick('pre_clearance_category_id', 'Check')}
                  onClose={() => handleButtonClick('pre_clearance_category_id', 'Close')}
                />
              )} */}
            </Input.Group>
          </Form.Item>
        </BaseCol>

        <BaseCol xs={24} md={24} lg={12}>
          <Form.Item
            label={
              <Space style={{ display: 'flex', alignItems: 'center' }}>
                {/* {shouldShowStatusIcons &&
                  (acceptedFields['country_ids'] === true ? (
                    <CheckCircleOutlined style={{ color: 'green', marginRight: '8px' }} />
                  ) : acceptedFields['country_ids'] === false ? (
                    <CloseCircleOutlined style={{ color: 'red', marginRight: '8px' }} />
                  ) : null)} */}
                Countries
              </Space>
            }
            name="country_ids"
          >
            <Input.Group compact style={{ display: 'flex' }}>
              {canEditFields ? (
                <BaseSelect
                  mode="multiple"
                  allowClear
                  showSearch
                  placeholder="Select Countries"
                  disabled={!canEditFields || !!acceptedFields['country_ids']}
                  options={countries}
                  style={{ width: '100%' }}
                  value={form.getFieldValue('country_ids')}
                  onChange={(value) => form.setFieldsValue({ country_ids: value })}
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                />
              ) : (
                <BaseInput
                  readOnly
                  value={
                    // Map the selected country ids to their names
                    form.getFieldValue('country_ids')?.length > 0
                      ? form
                          .getFieldValue('country_ids')
                          .map((id: any) => countries.find((country) => country.value === id)?.label)
                          .join(', ') || 'Not Selected'
                      : 'Not Selected'
                  }
                />
              )}
              {/* {shouldShowButtonControls && (
                <ButtonGroup
                  onCheck={() => handleButtonClick('country_ids', 'Check')}
                  onClose={() => handleButtonClick('country_ids', 'Close')}
                />
              )} */}
            </Input.Group>
          </Form.Item>
        </BaseCol>
        <BaseCol xs={24} md={24} lg={12}>
          <Form.Item
            label={
              <Space style={{ display: 'flex', alignItems: 'center' }}>
                {/* {shouldShowStatusIcons &&
                  (acceptedFields['location_id'] === true ? (
                    <CheckCircleOutlined style={{ color: 'green', marginRight: '8px' }} />
                  ) : acceptedFields['location_id'] === false ? (
                    <CloseCircleOutlined style={{ color: 'red', marginRight: '8px' }} />
                  ) : null)} */}
                Location
              </Space>
            }
            name="location_id"
          >
            <Input.Group compact style={{ display: 'flex' }}>
              {canEditFields ? (
                <BaseSelect
                  allowClear
                  placeholder="Select Location"
                  disabled={!canEditFields || !!acceptedFields['location_id']}
                  options={location_options}
                  style={{ width: '100%' }}
                  value={form.getFieldValue('location_id')}
                  onChange={(value) => form.setFieldsValue({ location_id: value })}
                />
              ) : (
                <BaseInput
                  readOnly
                  value={
                    form.getFieldValue('location_id')
                      ? location_options.find((location) => location.value === form.getFieldValue('location_id'))
                          ?.label || 'Not Selected'
                      : 'Not Selected'
                  }
                />
              )}
              {/* {shouldShowButtonControls && (
                <ButtonGroup
                  onCheck={() => handleButtonClick('location_id', 'Check')}
                  onClose={() => handleButtonClick('location_id', 'Close')}
                />
              )} */}
            </Input.Group>
          </Form.Item>
        </BaseCol>
        <BaseCol xs={24} md={24} lg={12}>
          <Form.Item
            label={
              <Space style={{ display: 'flex', alignItems: 'center' }}>
                {/* {shouldShowStatusIcons &&
                  (acceptedFields['department_id'] === true ? (
                    <CheckCircleOutlined style={{ color: 'green', marginRight: '8px' }} />
                  ) : acceptedFields['department_id'] === false ? (
                    <CloseCircleOutlined style={{ color: 'red', marginRight: '8px' }} />
                  ) : null)} */}
                Department
              </Space>
            }
            name="department_id"
          >
            <Input.Group compact style={{ display: 'flex' }}>
              {canEditFields ? (
                <BaseSelect
                  allowClear
                  placeholder="Select Department"
                  disabled={!canEditFields || !!acceptedFields['department_id']}
                  options={department_options}
                  style={{ width: '100%' }}
                  value={form.getFieldValue('department_id')}
                  onChange={(value) => form.setFieldsValue({ department_id: value })}
                />
              ) : (
                <BaseInput
                  readOnly
                  value={
                    form.getFieldValue('department_id')
                      ? department_options.find(
                          (department) => department.value === form.getFieldValue('department_id'),
                        )?.label || 'Not Selected'
                      : 'Not Selected'
                  }
                />
              )}
              {/* {shouldShowButtonControls && (
                <ButtonGroup
                  onCheck={() => handleButtonClick('department_id', 'Check')}
                  onClose={() => handleButtonClick('department_id', 'Close')}
                />
              )} */}
            </Input.Group>
          </Form.Item>
        </BaseCol>

        {fields?.map((e: any) => (
          <React.Fragment key={e.name}>
            <Col xs={24} md={24} lg={12}>
              <Form.Item
                name={e?.name}
                label={
                  <Space style={{ display: 'flex', alignItems: 'center' }}>
                    {/* {shouldShowStatusIcons &&
                      (acceptedFields[e?.name] === true ? (
                        <CheckCircleOutlined style={{ color: 'green', marginRight: '8px' }} />
                      ) : acceptedFields[e?.name] === false ? (
                        <CloseCircleOutlined style={{ color: 'red', marginRight: '8px' }} />
                      ) : null)} */}
                    {e?.label}
                  </Space>
                }
              >
                {e?.component}
              </Form.Item>
            </Col>
          </React.Fragment>
        ))}
      </Row>
    </>
  );
};

export default PreClearanceInfo;
